import { useSearchParams } from "react-router-dom";
import { Phone, Mail, Linkedin, MapPin } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { SEO } from "../components/site/SEO";
import { ContactForm } from "../components/site/ContactForm";
import { useContent } from "../context/ContentContext";

export default function Contact() {
  const [params] = useSearchParams();
  const presetService = params.get("service") || "";
  const { content } = useContent();
  const contact = content.contact;

  return (
    <SiteLayout>
      <SEO
        title="Contact Fourbees Consultancy — Let's Talk About Your Business"
        description="Get in touch with Fourbees Consultancy. Doorstep dairy consultancy and small business support across the UK. Personal reply from Simon within one working day."
        path="/contact"
      />

      {/* Hero */}
      <section className="bg-cream py-24 md:py-32" data-testid="contact-hero">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="section-number mb-3">Contact</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-navy leading-[1.0] tracking-[-0.025em]">
                Let's talk about <br />
                <span className="text-gold">your business.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="font-body text-base md:text-lg text-charcoal/75 leading-relaxed">
                Whether you're looking to grow your milk round, improve your operations or
                simply need some practical advice, we'd be pleased to hear from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + details */}
      <section className="bg-cream border-t border-light-grey py-20 md:py-24" data-testid="contact-main">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">— Direct contact</div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy leading-tight tracking-tight mb-8">
                Prefer to call or email?
              </h2>

              <div className="space-y-7">
                <a
                  href={`tel:${contact.phone.replace(/\s/g, "")}`}
                  className="flex items-start gap-5 group"
                  data-testid="contact-page-phone"
                >
                  <div className="w-11 h-11 bg-navy text-gold rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-charcoal/50 mb-1">
                      Telephone
                    </div>
                    <div className="font-heading text-xl font-bold text-navy group-hover:text-gold transition-colors">
                      {contact.phone}
                    </div>
                  </div>
                </a>

                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-start gap-5 group"
                  data-testid="contact-page-email"
                >
                  <div className="w-11 h-11 bg-gold text-navy rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail size={18} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-charcoal/50 mb-1">
                      Email
                    </div>
                    <div className="font-heading text-xl font-bold text-navy group-hover:text-gold transition-colors break-all">
                      {contact.email}
                    </div>
                  </div>
                </a>

                <a
                  href={contact.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-5 group"
                  data-testid="contact-page-linkedin"
                >
                  <div className="w-11 h-11 bg-white border border-light-grey text-navy rounded-sm flex items-center justify-center flex-shrink-0">
                    <Linkedin size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-charcoal/50 mb-1">
                      LinkedIn
                    </div>
                    <div className="font-heading text-xl font-bold text-navy group-hover:text-gold transition-colors">
                      Connect with {contact.founderName}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 bg-white border border-light-grey text-navy rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-charcoal/50 mb-1">
                      Based in
                    </div>
                    <div className="font-heading text-xl font-bold text-navy">
                      {contact.location}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 border border-light-grey rounded-sm bg-white">
                <h3 className="font-heading text-base font-bold text-navy mb-2">
                  What happens next?
                </h3>
                <ol className="space-y-2 text-sm text-charcoal/70 font-body list-decimal pl-5">
                  <li>Simon personally reads every enquiry.</li>
                  <li>You get a reply within one working day.</li>
                  <li>We arrange a short call to understand the detail.</li>
                  <li>No obligation, no sales pressure.</li>
                </ol>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService={presetService} />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
